import { useEffect, useState } from "react";
import { vatApi, businessesApi } from "@/lib/api";
import { useAuth, isMaster, userBusinessIds } from "@/lib/auth";
import { toast } from "sonner";
import { Trash2, RefreshCcw, Loader2 } from "lucide-react";

export default function VATCodes() {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [bizId, setBizId] = useState("");
  const [vats, setVats] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const biz = businesses.find((b) => b.id === bizId);

  useEffect(() => {
    businessesApi.list().then((b) => {
      setBusinesses(b);
      setBizId(isMaster(user) ? (b[0]?.id || "") : userBusinessIds(user)[0] || "");
    });
  }, [user]);

  useEffect(() => { if (bizId) vatApi.list(bizId, { include_hidden: true }).then(setVats); }, [bizId]);

  const del = async (id) => {
    if (!window.confirm("Delete this VAT code?")) return;
    await vatApi.del(id);
    vatApi.list(bizId, { include_hidden: true }).then(setVats);
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await vatApi.syncFromAccounting(bizId);
      toast.success(`Synced ${res.total} tax rates (+${res.created} new, ~${res.updated} updated)`);
      vatApi.list(bizId, { include_hidden: true }).then(setVats);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-bold text-espresso">VAT / Tax codes</h1>
          <p className="text-espresso-ink/60 mt-2">Read-only view. Tax rates sync from {biz?.accounting === "xero" ? "Xero" : "Zoho Books"} via Accounting → Master sync. Manual entries are no longer supported.</p>
        </div>
        <div className="flex gap-2">
          {(isMaster(user) || userBusinessIds(user).length > 1) && (
            <select data-testid="vat-biz" value={bizId} onChange={(e) => setBizId(e.target.value)} className="px-3 py-2 bg-white border border-canvas-border text-sm brand-focus">
              {(isMaster(user) ? businesses : businesses.filter((b) => userBusinessIds(user).includes(b.id))).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
        </div>
      </div>
      <div className="text-xs text-espresso-ink/50">
        Tax-rate sync runs from <b>Accounting → Master sync</b>.
      </div>

      {!biz?.connected && (
        <div className="p-3 bg-status-pending/10 border border-status-pending/30 text-status-pending text-sm">
          Connect {biz?.accounting === "xero" ? "Xero" : "Zoho Books"} first to enable tax rate sync.
        </div>
      )}

      <div className="bg-white border border-canvas-border overflow-hidden shadow-soft">
          <table className="w-full">
            <thead className="bg-canvas text-xs uppercase tracking-wider text-espresso-ink/60">
              <tr>
                <th className="text-left px-6 py-3">Code</th>
                <th className="text-left px-6 py-3">Name</th>
                <th className="text-left px-6 py-3">Rate</th>
                <th className="text-left px-6 py-3">External ID</th>
                <th className="text-left px-6 py-3 w-32">Visibility</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {vats.map((v) => (
                <tr key={v.id} className={`border-t border-canvas-border ${v.hidden ? "bg-canvas/60 text-espresso-ink/50" : ""}`}>
                  <td className="px-6 py-3 font-medium text-espresso">{v.code}</td>
                  <td className="px-6 py-3 text-sm text-espresso-ink">{v.name}</td>
                  <td className="px-6 py-3 text-sm text-espresso-ink">{v.rate}%</td>
                  <td className="px-6 py-3 text-xs font-mono text-espresso-ink/60">{v.external_id || "—"}</td>
                  <td className="px-6 py-3">
                    {isMaster(user) ? (
                      <button
                        data-testid={`vat-toggle-hidden-${v.id}`}
                        onClick={async () => {
                          try {
                            await vatApi.setHidden(v.id, !v.hidden);
                            toast.success(v.hidden ? "Now visible to users" : "Now hidden from users");
                            vatApi.list(bizId).then(setVats);
                          } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                        }}
                        className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold transition-colors ${v.hidden ? "bg-status-duplicate/10 text-status-duplicate border border-status-duplicate/40 hover:bg-status-duplicate/20" : "bg-status-synced/10 text-status-synced border border-status-synced/40 hover:bg-status-synced/20"}`}
                      >
                        {v.hidden ? "Hidden" : "Visible"}
                      </button>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest text-espresso-ink/50">Visible</span>
                    )}
                  </td>
                  <td className="px-3 py-3"><button onClick={() => del(v.id)} className="text-status-duplicate hover:opacity-70"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
      </div>
    </div>
  );
}
